import { useEffect, useState } from "react";
import { Link, useParams, useLocation } from "wouter";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { useConvexUser } from "@/hooks/useConvexUser";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Users, Loader2 } from "lucide-react";
import { SEO } from "@/components/SEO";

export default function JoinTeam() {
  const params = useParams();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { authLoading, userId } = useConvexUser();

  const search = typeof window !== "undefined" ? window.location.search : "";
  const sp = new URLSearchParams(search);
  const token = (params.token as string | undefined) || sp.get("token") || "";
  const initialCode = sp.get("code") || "";

  const [code, setCode] = useState(initialCode);
  const [joining, setJoining] = useState(false);

  const invitePreview = useQuery(
    api.teams.getInvitationByToken,
    token ? { token } : "skip",
  );
  const codePreview = useQuery(
    api.teams.getTeamByInviteCode,
    code.trim() ? { code: code.trim() } : "skip",
  );
  const ctx = useQuery(
    api.teams.getTeamContext,
    userId ? { userId } : "skip",
  );

  const acceptByToken = useMutation(api.teams.acceptInviteByToken);
  const acceptByCode = useMutation(api.teams.acceptInviteByCode);

  // If already on a team, send them to the team page.
  useEffect(() => {
    if (ctx?.accountType === "team" && !joining) {
      // give them a moment then redirect
    }
  }, [ctx, joining]);

  if (authLoading || (userId && ctx === undefined)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const join = async () => {
    if (!userId) return;
    setJoining(true);
    try {
      if (token) {
        await acceptByToken({ userId, token });
      } else if (code.trim()) {
        await acceptByCode({ userId, code: code.trim() });
      } else {
        toast({ title: "Enter an invite code", variant: "destructive" });
        setJoining(false);
        return;
      }
      toast({ title: "Welcome to the team!" });
      navigate("/team");
    } catch (err: any) {
      toast({
        title: "Couldn't join",
        description: err?.message || "This invite may be invalid or expired.",
        variant: "destructive",
      });
    } finally {
      setJoining(false);
    }
  };

  const alreadyOnTeam = ctx?.accountType === "team";
  const previewName =
    (token && invitePreview?.teamName) ||
    (code.trim() && codePreview?.teamName) ||
    null;
  const previewRole =
    (token && invitePreview?.role) ||
    (code.trim() && codePreview?.role) ||
    null;
  const tokenInvalid =
    token && invitePreview !== undefined && invitePreview === null;
  const tokenUnusable =
    token &&
    invitePreview &&
    invitePreview.status !== "pending";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <SEO title="Join a team - LRScribe" description="Accept your invitation to join a team on LRScribe." />
      <Card className="max-w-md w-full" data-testid="card-join">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="h-6 w-6 text-primary" />
            </div>
          </div>
          <CardTitle data-testid="text-join-title">
            {previewName ? `Join ${previewName}` : "Join a team"}
          </CardTitle>
          <CardDescription>
            {previewRole
              ? `You'll join as ${previewRole === "admin" ? "an admin" : "a member"}.`
              : "Enter your invite code to join your colleagues."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {alreadyOnTeam ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                You're already part of a team.
              </p>
              <Link href="/team">
                <Button className="w-full" data-testid="button-go-team">Go to your team</Button>
              </Link>
            </div>
          ) : tokenInvalid ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-destructive">
                This invitation link is not valid.
              </p>
              <Link href="/dashboard">
                <Button variant="outline" className="w-full">Back to dashboard</Button>
              </Link>
            </div>
          ) : tokenUnusable ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-destructive">
                This invitation has already been used or revoked.
              </p>
              <Link href="/dashboard">
                <Button variant="outline" className="w-full">Back to dashboard</Button>
              </Link>
            </div>
          ) : (
            <>
              {!token && (
                <div className="space-y-2">
                  <Label htmlFor="join-code">Invite code</Label>
                  <Input
                    id="join-code"
                    data-testid="input-join-code"
                    placeholder="ABC123"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                  />
                  {code.trim() && codePreview === null && (
                    <p className="text-xs text-destructive">
                      That code isn't valid.
                    </p>
                  )}
                </div>
              )}
              <Button
                className="w-full"
                onClick={join}
                disabled={joining || (!token && !code.trim())}
                data-testid="button-join"
              >
                {joining && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {previewName ? `Join ${previewName}` : "Join team"}
              </Button>
              <Link href="/dashboard">
                <Button variant="ghost" className="w-full" data-testid="button-cancel-join">
                  Cancel
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
